'use client';

import { useCallback, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { InfiniteCombobox } from '@/components/forms/infinite-combobox';
import { useInfiniteSelect } from '@/hooks/use-infinite-select';
import { STALE_TIME } from '@/lib/query/stale-times';
import { deviceKeys, devicesApi, type Device } from '@/features/devices';
import {
  operatorKeys,
  operatorsApi,
  type Operator
} from '@/features/operators';
import { useUpdateAssignmentMutation } from '../services/handover-scanning.mutations';
import type { WorkloadAssignment } from '../types';

interface EditAssignmentDialogProps {
  assignment: WorkloadAssignment | null;
  onOpenChange: (open: boolean) => void;
}

interface EditFormProps {
  assignment: WorkloadAssignment;
  onOpenChange: (open: boolean) => void;
}

function EditForm({ assignment, onOpenChange }: EditFormProps) {
  const t = useTranslations('handoverScanning');
  const [deviceId, setDeviceId] = useState(String(assignment.device_id));
  const [operatorId, setOperatorId] = useState(String(assignment.operator_id));

  const devicesSelect = useInfiniteSelect<Device>({
    queryKey: deviceKeys.lists(),
    queryFn: (params) => devicesApi.getAll(params),
    mapOption: useCallback(
      (d: Device) => ({
        value: String(d.id),
        label: d.serial_number ? `${d.serial_number} — ${d.name}` : d.name
      }),
      []
    ),
    // Not reference data: these two lists are filtered to what is *currently*
    // free, and any other supervisor's assignment consumes an entry from them
    // (`useCreateAssignmentMutation` invalidates both keys for exactly that
    // reason — but only in the tab that made the call). Reassigning off a
    // 5-minute-old list picks a scanner or operator someone else already took.
    staleTime: STALE_TIME.live,
    params: { status: ['online'] }
  });

  const operatorsSelect = useInfiniteSelect<Operator>({
    queryKey: operatorKeys.lists(),
    queryFn: (params) => operatorsApi.getAll(params),
    mapOption: useCallback(
      (o: Operator) => ({
        value: String(o.id),
        label: o.name ?? `#${o.id}`
      }),
      []
    ),
    staleTime: STALE_TIME.live,
    params: { availability: ['available'] }
  });

  // Labels for the currently-assigned device/operator so the combobox shows a
  // value before its page has loaded.
  const currentDeviceLabel = assignment.device
    ? assignment.device.serial_number
      ? `${assignment.device.serial_number} — ${assignment.device.name}`
      : assignment.device.name
    : undefined;
  const currentOperatorLabel = assignment.operator?.name ?? undefined;

  const updateMutation = useUpdateAssignmentMutation();

  async function handleSave() {
    try {
      await updateMutation.mutateAsync({
        id: assignment.id,
        data: {
          device_id: deviceId ? Number(deviceId) : undefined,
          operator_id: operatorId ? Number(operatorId) : undefined
        }
      });
      onOpenChange(false);
    } catch {
      // handled by global MutationCache
    }
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>{t('editAssignmentTitle')}</DialogTitle>
        <DialogDescription>{t('editAssignmentDescription')}</DialogDescription>
      </DialogHeader>

      <div className='space-y-4 py-2'>
        {/* Device select */}
        <div className='space-y-2'>
          <Label>{t('selectedScanner')}</Label>
          <InfiniteCombobox
            value={deviceId}
            onChange={setDeviceId}
            placeholder={t('searchScanners')}
            infiniteConfig={devicesSelect}
            displayValue={
              deviceId === String(assignment.device_id)
                ? currentDeviceLabel
                : undefined
            }
          />
        </div>

        {/* Operator select */}
        <div className='space-y-2'>
          <Label>{t('selectedOperator')}</Label>
          <InfiniteCombobox
            value={operatorId}
            onChange={setOperatorId}
            placeholder={t('searchOperators')}
            infiniteConfig={operatorsSelect}
            displayValue={
              operatorId === String(assignment.operator_id)
                ? currentOperatorLabel
                : undefined
            }
          />
        </div>
      </div>

      <DialogFooter>
        <Button
          variant='outline'
          onClick={() => onOpenChange(false)}
          disabled={updateMutation.isPending}
        >
          {t('cancel')}
        </Button>
        <Button onClick={handleSave} disabled={updateMutation.isPending}>
          {updateMutation.isPending && (
            <Loader2 className='me-2 h-4 w-4 animate-spin' />
          )}
          {t('save')}
        </Button>
      </DialogFooter>
    </>
  );
}

export function EditAssignmentDialog({
  assignment,
  onOpenChange
}: EditAssignmentDialogProps) {
  return (
    <Dialog open={!!assignment} onOpenChange={onOpenChange}>
      <DialogContent>
        {assignment && (
          <EditForm
            key={assignment.id}
            assignment={assignment}
            onOpenChange={onOpenChange}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
